import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import moment from 'moment';
import DebouncedSelect from 'components/DebounceSelect/DebounceSelect';
import { getTrainingCourseOptions, getTrainingCourseDetail } from '../../../api/services/training';

const TrainingForm = ({
  state,
  dispatch,
  updateFieldCreator,
  errors,
  removeError,
  onSubmit,
  heading,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  // Set when the user picks a course themselves, as opposed to the edit screen
  // filling one in. Only their choice resets the fields that belong to the
  // course; hydrating an existing training must leave them alone.
  const courseChosenByUser = useRef(false);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  /**
   * A course option reads as "Name (Organisation)": a member association runs
   * the same course through several organisations, and the name alone does not
   * say which one a training belongs to.
   */
  const courseOptionLabel = (item) =>
    item.organizationName ? `${item.name} (${item.organizationName})` : item.name;

  const handleChangeCourse = (option) => {
    dispatch(updateFieldCreator({ name: 'course', value: option?.value }));
    if (errors.course) removeError(clearErrors(errors, 'course'));
    courseChosenByUser.current = true;
  };

  /**
   * Drop the competencies picked for the previously selected course: they belong
   * to it, are no longer offered in the field, and the backend refuses them — so
   * leaving them in would only fail the save.
   *
   * Does nothing unless the user changed the course, so opening an existing
   * training keeps what it was saved with.
   */
  const dropCompetenciesOfPreviousCourse = () => {
    if (!courseChosenByUser.current) {
      return;
    }

    dispatch(updateFieldCreator({ name: 'competencies', value: [] }));
    courseChosenByUser.current = false;
  };

  // What belongs to the course: the competencies a training on it may claim, and
  // whether it is a programme. The control number is not asked for here — the
  // system issues it from the series the member association configured.
  useEffect(() => {
    if (!state.course) {
      dispatch(updateFieldCreator({ name: 'competencyOptions', value: [] }));
      dispatch(updateFieldCreator({ name: 'isProgramme', value: false }));
      dropCompetenciesOfPreviousCourse();
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await (await getTrainingCourseDetail(state.course)).json();
        if (!active || !res?.results?.result) return;
        const { competencies, isProgramme } = res.results.result;
        dispatch(
          updateFieldCreator({
            name: 'competencyOptions',
            value: (competencies || []).map((c) => ({
              value: `${c.type}:${c.id}`,
              label: c.name,
              type: c.type,
              entityId: c.id,
            })),
          }),
        );
        dispatch(updateFieldCreator({ name: 'isProgramme', value: !!isProgramme }));
        dropCompetenciesOfPreviousCourse();
      } catch (error) {
        console.error('Error loading course detail:', error);
      }
    })();

    return () => {
      active = false;
    };
  }, [state.course]);


  const submit = () => {
    const groupedCompetencies = {
      competency: [],
      peopleCompetency: [],
      practiceCompetency: [],
      customCourseCompetency: [],
    };
    (state.competencies || []).forEach((option) => {
      if (groupedCompetencies[option.type]) {
        groupedCompetencies[option.type].push(option.entityId);
      }
    });

    const body = {
      fullName: state.fullName,
      email: state.email,
      course: state.course,
      name: state.name,
      completionDate: state.completionDate
        ? moment(state.completionDate).format('YYYY-MM-DD')
        : null,
      cpdHours: state.cpdHours === '' ? null : state.cpdHours,
      competencies: groupedCompetencies.competency,
      peopleCompetencies: groupedCompetencies.peopleCompetency,
      practiceCompetencies: groupedCompetencies.practiceCompetency,
      customCourseCompetencies: groupedCompetencies.customCourseCompetency,
      programmeSubset: state.isProgramme ? state.programmeSubset : null,
    };

    onSubmit(body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{heading}</Heading>
          </Margin>
          {/*
            The recipient is typed in, not picked from the accounts: member
            associations train people who have never signed in. The email is
            what an account is matched to when one appears.
          */}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="fullName"
                  label={t('page.ma.cpdManagement.trainings.fullName')}
                  required
                  error={errors.fullName}
                >
                  <input
                    id="fullName"
                    type="text"
                    placeholder={t('page.ma.cpdManagement.trainings.fullNamePlaceholder')}
                    value={state.fullName}
                    onChange={handleChangeNative('fullName', 'fullName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="email"
                  label={t('page.ma.cpdManagement.trainings.email')}
                  required
                  error={errors.email}
                >
                  <input
                    id="email"
                    type="email"
                    placeholder={t('page.ma.cpdManagement.trainings.emailPlaceholder')}
                    value={state.email}
                    onChange={handleChangeNative('email', 'email')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="course"
                  label={t('page.ma.cpdManagement.trainings.course')}
                  required
                  error={errors.course}
                >
                  <DebouncedSelect
                    id="course"
                    value={state.course}
                    intialSelectedOption={state.courseOption}
                    onChange={handleChangeCourse}
                    placeholder={t('page.ma.cpdManagement.trainings.coursePlaceholder')}
                    fetchOptions={getTrainingCourseOptions}
                    formatLabel={courseOptionLabel}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="name"
                  label={t('page.ma.cpdManagement.trainings.name')}
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    placeholder={t('page.ma.cpdManagement.trainings.namePlaceholder')}
                    value={state.name}
                    onChange={handleChangeNative('name', 'name')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="completionDate"
                  label={t('page.ma.cpdManagement.trainings.completionDate')}
                  required
                  value={state.completionDate}
                  clear={() => handleChangeCustom('completionDate')(null)}
                  error={errors.completionDate}
                >
                  <Datepicker
                    id="completionDate"
                    type="text"
                    small
                    placeholderText={t('page.ma.cpdManagement.trainings.completionDate')}
                    selected={state.completionDate}
                    onChange={handleChangeCustom('completionDate', 'completionDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cpdHours"
                  label={t('page.ma.cpdManagement.trainings.cpdHours')}
                  error={errors.cpdHours}
                >
                  <input
                    id="cpdHours"
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder={t('page.ma.cpdManagement.trainings.cpdHoursPlaceholder')}
                    value={state.cpdHours}
                    onChange={handleChangeNative('cpdHours', 'cpdHours')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="competencies"
                  label={t('page.ma.cpdManagement.trainings.competencies')}
                  error={errors.competencies}
                >
                  <Select
                    id="competencies"
                    isMulti
                    placeholder={
                      state.course
                        ? t('page.ma.cpdManagement.trainings.competenciesPlaceholder')
                        : t('page.ma.cpdManagement.trainings.competenciesSelectCourseFirst')
                    }
                    value={state.competencies}
                    options={state.competencyOptions}
                    onChange={(options) =>
                      handleChangeCustom('competencies', 'competencies')(options || [])
                    }
                    disabled={!state.course}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          {state.isProgramme && (
            <Row>
              <Col size={{ xl: 1 }}>
                <Margin bottom="20">
                  <Field
                    id="programmeSubset"
                    label={t('page.ma.cpdManagement.trainings.programmeSubset')}
                    error={errors.programmeSubset}
                  >
                    <textarea
                      id="programmeSubset"
                      rows={4}
                      placeholder={t('page.ma.cpdManagement.trainings.programmeSubsetPlaceholder')}
                      value={state.programmeSubset}
                      onChange={handleChangeNative('programmeSubset', 'programmeSubset')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

TrainingForm.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  errors: PropTypes.object,
  removeError: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  heading: PropTypes.string,
};

TrainingForm.defaultProps = {
  errors: {},
  heading: '',
};

export default TrainingForm;
